import React, { useContext, useEffect } from "react"
import { Form, Select } from "semantic-ui-react"
import { BookerContext } from "../../../contexts/BookerContext"
import { CampProps } from "../../../interfaces"
import pluralize from "../../../utils/pluralize"
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline"
import classNames from "../../../utils/classNames"

interface ComponentProps {
  camp: CampProps
  userPassengers: number
  handlePassengersChange: (event, more) => any
  userKidCampers: number
  handleKidCampersChange: (event, more) => void
  kidError: string
  setKidError: (v: string) => void
  adultsError: string
  setAdultsError: (v: string) => void
}

const PassengersField = ({
  camp,
  userPassengers,
  handlePassengersChange,
  userKidCampers,
  handleKidCampersChange,
  kidError,
  adultsError,
  setKidError,
  setAdultsError,
}: ComponentProps) => {
  const { campingStyle, isKidsPrereqPresent } = useContext(BookerContext)

  // Hardcode increased passenger limit for Camps, which books the whole campground for up to 150 people at a time
  let numberOfPossibleAdults =
    campingStyle.adults_capacity || (["camp-cottonwood", "camp-currie"].includes(camp.slug) ? 300 : 20)
  let numberOfPossibleKids = campingStyle.kids_capacity || numberOfPossibleAdults
  if (campingStyle.guest_capacity) {
    numberOfPossibleAdults = campingStyle.adults_capacity
      ? Math.min(campingStyle.adults_capacity, campingStyle.guest_capacity - (userKidCampers || 0))
      : campingStyle.guest_capacity - (userKidCampers || 0)
    numberOfPossibleKids = campingStyle.kids_capacity
      ? Math.min(campingStyle.kids_capacity, campingStyle.guest_capacity - (userPassengers || 0))
      : campingStyle.guest_capacity - (userPassengers || 0)
  }

  const guestCapacityError =
    campingStyle.guest_capacity && campingStyle.guest_capacity <= (userKidCampers || 0) + (userPassengers || 0)

  const isPassengersMinusDisabled = !userPassengers || userPassengers <= 1
  const isPassengersPlusDisabled = userPassengers === numberOfPossibleAdults || guestCapacityError
  const passengerError = guestCapacityError
    ? `${campingStyle.guest_capacity} guests max`
    : `${numberOfPossibleAdults} adults max`

  const isKidsMinusDisabled = !userKidCampers || userKidCampers <= 0
  const isKidsPlusDisabled = userKidCampers === numberOfPossibleKids || guestCapacityError
  const kidsError = guestCapacityError
    ? `${campingStyle.guest_capacity} guests max`
    : `${numberOfPossibleKids} kids max`

  useEffect(() => {
    if (userPassengers > numberOfPossibleAdults) {
      handlePassengersChange(null, { value: numberOfPossibleAdults })
    }

    if (userKidCampers > numberOfPossibleKids) {
      handleKidCampersChange(null, { value: numberOfPossibleKids })
    }
  }, [])

  return (
    <>
      <div>
        <div className="flex justify-between items-center ">
          <Form.Field required className="flex flex-col">
            <div className="font-bold text-lg mb-1">{isKidsPrereqPresent ? "Adults" : "Guests"}</div>
            {/* TODO: make this dynamic based on extra_passenger_age */}
            {campingStyle.extra_passenger_age && (
              <div className="text-md xs:text-base">Ages {campingStyle.extra_passenger_age} and up</div>
            )}
          </Form.Field>
          <div className="flex items-center flex-col xs:flex-row-reverse">
            <div className="flex items-center">
              <div
                className=""
                onClick={(e) => {
                  !isPassengersMinusDisabled && handlePassengersChange(e, { value: userPassengers - 1 })
                  setAdultsError("")
                }}
              >
                <MinusCircleIcon
                  className={classNames("w-8 h-8 cursor-pointer", isPassengersMinusDisabled && "text-gray-300")}
                />
              </div>
              <input
                type="number"
                aria-label="adult passenger count"
                value={userPassengers}
                className="numberInputWithoutArrows text-center"
                onChange={(e) => {
                  setAdultsError("")
                  const value = Number(e.target.value)

                  if (value < 0) {
                    handlePassengersChange(e, { value: 0 })
                  } else if (value > numberOfPossibleAdults) {
                    handlePassengersChange(e, { value: numberOfPossibleAdults })
                  } else {
                    handlePassengersChange(e, { value })
                  }
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  width: 55,
                  padding: 0,
                  fontSize: 18,
                }}
              />
              <div
                className=""
                onClick={(e) => {
                  setAdultsError("")
                  !isPassengersPlusDisabled && handlePassengersChange(e, { value: userPassengers + 1 })
                }}
              >
                <PlusCircleIcon
                  className={classNames("w-8 h-8 cursor-pointer", isPassengersPlusDisabled && "text-gray-300")}
                />
              </div>
            </div>

            {numberOfPossibleAdults && isPassengersPlusDisabled ? (
              <div className="top-2 xs:top-0 xs:mr-3 text-red-600 font-medium">{passengerError}</div>
            ) : (
              <></>
            )}
          </div>
        </div>
        {adultsError ? <div className="text-red-600 font-semibold">{adultsError}</div> : <></>}
      </div>
      {isKidsPrereqPresent && (
        <div>
          <div className="flex justify-between items-center ">
            <Form.Field required className="flex flex-col">
              <div className="font-bold text-lg mb-1">Kids</div>
              {campingStyle.extra_kid_campers_age && campingStyle.extra_passenger_age ? (
                <div className="text-md xs:text-base">
                  Ages {campingStyle.extra_kid_campers_age} - {campingStyle.extra_passenger_age}
                </div>
              ) : (
                campingStyle.extra_kid_campers_age && (
                  <div className="text-md xs:text-base">Ages {campingStyle.extra_kid_campers_age} and up</div>
                )
              )}
            </Form.Field>
            <div className="flex items-center flex-col xs:flex-row-reverse">
              <div className="flex items-center">
                <div
                  className=""
                  onClick={(e) => {
                    {
                      setKidError("")

                      !isKidsMinusDisabled && handleKidCampersChange(e, { value: userKidCampers - 1 })
                    }
                  }}
                >
                  <MinusCircleIcon
                    className={classNames("w-8 h-8 cursor-pointer", isKidsMinusDisabled && "text-gray-300")}
                  />
                </div>
                <input
                  type="number"
                  aria-label="kid passenger count"
                  value={userKidCampers}
                  className="numberInputWithoutArrows text-center"
                  onChange={(e) => {
                    setKidError("")
                    const value = Number(e.target.value)

                    if (value < 0) {
                      handleKidCampersChange(e, { value: 0 })
                    } else if (value > numberOfPossibleKids) {
                      handleKidCampersChange(e, { value: numberOfPossibleKids })
                    } else {
                      handleKidCampersChange(e, { value })
                    }
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    width: 55,
                    padding: 0,
                    fontSize: 18,
                  }}
                />
                <div
                  className=""
                  onClick={(e) => {
                    setKidError("")
                    !isKidsPlusDisabled && handleKidCampersChange(e, { value: userKidCampers + 1 })
                  }}
                >
                  <PlusCircleIcon
                    className={classNames("w-8 h-8 cursor-pointer", isKidsPlusDisabled && "text-gray-300")}
                  />
                </div>
              </div>
              {numberOfPossibleKids && isKidsPlusDisabled ? (
                <div className="top-2 xs:top-0 xs:mr-3 text-red-600 font-medium">{kidsError}</div>
              ) : (
                <></>
              )}
            </div>
          </div>
          {kidError ? <div className="text-red-600 font-semibold">{kidError}</div> : <></>}
        </div>
      )}
    </>
  )
}

export default PassengersField
